import { defineStore } from 'pinia'
import { ref, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { initializeEcho, disconnectEcho } from '@/services/echo'
import { i18nLocaleToUrl, getCurrentLocale } from '@/plugins/i18n'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const toast = useToast()
  
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token'))
  const isLoading = ref(false)
  
  // Getters
  const isAuthenticated = computed(() => {
    try {
      return !!token.value && !!user.value
    } catch (error) {
      console.warn('Error checking authentication:', error)
      return false
    }
  })
  
  const isAdmin = computed(() => {
    try {
      return user.value?.role === 'admin'
    } catch (error) {
      console.warn('Error checking admin status:', error)
      return false
    }
  })
  
  const userSubscription = computed(() => {
    try {
      return user.value?.active_subscription || null
    } catch (error) {
      console.warn('Error getting user subscription:', error)
      return null
    }
  })
  
  const subscriptionPlan = computed(() => {
    try {
      return userSubscription.value?.subscription_plan || null
    } catch (error) {
      console.warn('Error getting subscription plan:', error)
      return null
    }
  })
  
  // Actions
  const login = async (credentials) => {
    isLoading.value = true
    try {
      const response = await axios.post('/auth/login', credentials)
      
      if (response.data.token) {
        token.value = response.data.token
        user.value = response.data.user
        
        localStorage.setItem('auth_token', response.data.token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        
        console.log('Login successful, auth state set:', { 
          hasToken: !!token.value, 
          hasUser: !!user.value,
          userRole: user.value?.role 
        })
        
        // Re-enable frontend logging after successful authentication
        const logger = (await import('@/services/logger')).default
        logger.enable()
        
        // Initialize Echo for real-time notifications
        initializeEcho(response.data.token)
        
        toast.success('Welcome back!')

        // Wait for router to be ready before navigating
        await router.isReady()

        // Use nextTick to ensure reactive state is fully updated
        await nextTick()

        // Get current locale for redirects
        const urlLocale = i18nLocaleToUrl(getCurrentLocale())

        // Redirect to appropriate dashboard based on role
        let redirect = router.currentRoute.value.query.redirect
        if (!redirect) {
          const dashboardPath = response.data.user.role === 'admin' ? '/admin/dashboard' : '/app/dashboard'
          redirect = `/${urlLocale}${dashboardPath}`
        }

        console.log('[Auth] Navigating to:', redirect, {
          hasToken: !!token.value,
          hasUser: !!user.value,
          userRole: user.value?.role
        })

        router.push(redirect)
      }
      
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const register = async (userData) => {
    isLoading.value = true
    try {
      const payload = {
        name: `${userData.first_name} ${userData.last_name}`.trim(),
        email: userData.email,
        password: userData.password,
        password_confirmation: userData.password_confirmation,
      }
      const response = await axios.post('/auth/register', payload)
      
      if (response.data.token) {
        token.value = response.data.token
        user.value = response.data.user
        
        localStorage.setItem('auth_token', response.data.token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        
        // Re-enable frontend logging after successful registration
        const logger = (await import('@/services/logger')).default
        logger.enable()
        
        // Initialize Echo for real-time notifications
        initializeEcho(response.data.token)
        
        toast.success('Account created successfully!')
        const urlLocale = i18nLocaleToUrl(getCurrentLocale())
        router.push(`/${urlLocale}/app/dashboard`)
      }
      
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed'
      toast.error(message)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const logout = async () => {
    try {
      if (token.value) {
        await axios.post('/auth/logout')
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear state regardless of API call success
      token.value = null
      user.value = null
      
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      
      // Disable aggressive frontend logging when logged out
      const logger = (await import('@/services/logger')).default
      if (logger.failureCount >= 2) {
        logger.disable()
      }
      
      // Disconnect Echo
      disconnectEcho()
      
      toast.info('You have been logged out')
      const urlLocale = i18nLocaleToUrl(getCurrentLocale())
      router.push(`/${urlLocale}/auth/login`)
    }
  }
  
  const fetchUser = async () => {
    if (!token.value) return
    
    try {
      const response = await axios.get('/user')
      user.value = response.data
      localStorage.setItem('user', JSON.stringify(response.data))
    } catch (error) {
      console.error('Failed to fetch user:', error)
      logout()
      throw error
    }
  }
  
  const updateUser = (userData) => {
    user.value = { ...user.value, ...userData }
    localStorage.setItem('user', JSON.stringify(user.value))
  }
  
  const canAccessFeature = (feature) => {
    try {
      if (!user.value || !feature) return false
      
      // Trial users have access to all features
      if (user.value.is_on_trial) return true
      
      // Check subscription plan features
      return subscriptionPlan.value?.[feature] || false
    } catch (error) {
      console.warn('Error checking feature access:', error)
      return false
    }
  }
  
  const getRemainingEmails = () => {
    try {
      return user.value?.remaining_emails || 0
    } catch (error) {
      console.warn('Error getting remaining emails:', error)
      return 0
    }
  }
  
  const canSendBulkEmails = (count = 1) => {
    if (!user.value) return false
    
    const remaining = getRemainingEmails()
    return remaining >= count && canAccessFeature('api_access')
  }
  
  const checkAuthentication = () => {
    // Ensure we have both token and user data
    const hasToken = !!token.value
    const hasUser = !!user.value
    const hasStoredToken = !!localStorage.getItem('auth_token')
    const hasStoredUser = !!localStorage.getItem('user')
    
    // If we have stored data but not in memory, reinitialize
    if (!hasToken && !hasUser && hasStoredToken && hasStoredUser) {
      initializeFromStorage()
    }
    
    return !!token.value && !!user.value
  }
  
  // Initialize user from localStorage if available
  const initializeFromStorage = () => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('user')
    
    if (storedToken && storedUser) {
      try {
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        console.log('Auth store initialized from localStorage:', { token: !!token.value, user: !!user.value })
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        localStorage.removeItem('user')
        localStorage.removeItem('auth_token')
        token.value = null
        user.value = null
      }
    }
  }
  
  // Initialize immediately
  initializeFromStorage()
  
  return {
    // State
    user,
    token,
    isLoading,
    
    // Getters
    isAuthenticated,
    isAdmin,
    userSubscription,
    subscriptionPlan,
    
    // Actions
    login,
    register,
    logout,
    fetchUser,
    updateUser,
    canAccessFeature,
    getRemainingEmails,
    canSendBulkEmails,
    checkAuthentication
  }
})